import { delay } from "./apiRetry.js";
import { whenOnline, type StopSignal } from "./reconnect.js";
import type { KnownSessionRead, SessionRead } from "./sessionReader.js";

// Boot on the shared session reader (sessionReader.ts), both apps: a timeout
// at boot is not signed out.

const BOOT_RETRY_FIRST_MS = 1_000;
const BOOT_RETRY_MAX_MS = 30_000;

// Waits, then asks again, on a capped backoff, only with the app in the
// foreground and not known offline (#397: a refresh that cannot answer is
// not tried). Each read starts in the epoch of its time. Null once `cancelled()`.
async function retryUntilKnown<S>(
  read: () => Promise<SessionRead<S>>,
  cancelled: () => boolean,
  foreground: () => Promise<void>,
  offline: () => boolean,
  stop: StopSignal | undefined,
): Promise<KnownSessionRead<S> | null> {
  for (let wait = BOOT_RETRY_FIRST_MS; ; ) {
    await delay(wait);
    await foreground();
    await whenOnline(offline, stop);
    if (cancelled()) return null;
    const answer = await read();
    if (cancelled()) return null;
    if (answer.kind !== "unknown") return answer;
    wait = Math.min(wait * 2, BOOT_RETRY_MAX_MS);
  }
}

export type BootSession<S, O> =
  | KnownSessionRead<S>
  // Unknown, with an account known on this device: the app starts with it,
  // offline. `settled` is the retry running behind: none there means Login.
  | { kind: "offline"; owner: O; settled: Promise<KnownSessionRead<S> | null> };

/** Boot: unknown is never signed out. With an account known on this device
 *  (`storedOwner`, no network) the app starts with it, so a gym with no
 *  signal still logs to the queue; without one, the splash retries, only
 *  once `foreground` resolves (app in front). Null once `cancelled()`.
 *  `offline()` true at boot (#397): the first read is skipped, it would wait
 *  for a token refresh that cannot answer; the session is unknown, and the
 *  retries wait for the network. Offline with no account on this device:
 *  none, Login at once, as the read answered before #397. The splash would
 *  wait for a reconnect that, on mobile, nothing announces yet (the bridge
 *  starts after the boot). `stop`, aborted with the boot, takes the retry
 *  off the reconnect signal. */
export async function bootSession<S, O>(
  read: () => Promise<SessionRead<S>>,
  storedOwner: () => O | null,
  cancelled: () => boolean,
  foreground: () => Promise<void> = () => Promise.resolve(),
  offline: () => boolean = () => false,
  stop?: StopSignal,
): Promise<BootSession<S, O> | null> {
  const offlineAtBoot = offline();
  const first: SessionRead<S> = offlineAtBoot ? { kind: "unknown" } : await read();
  if (cancelled()) return null;
  if (first.kind !== "unknown") return first;
  const owner = storedOwner();
  if (owner === null && offlineAtBoot) return { kind: "none" };
  const settled = retryUntilKnown(read, cancelled, foreground, offline, stop);
  return owner === null ? settled : { kind: "offline", owner, settled };
}
